/**
 * Extracts YouTube video ID from various YouTube URL formats
 * @param url - YouTube URL in any format
 * @returns Video ID or null if not a YouTube URL
 */
export const extractYouTubeVideoId = url => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(youtubeRegex);
  return match ? match[1] : null;
};

/**
 * Converts YouTube URL to thumbnail URL
 * @param url - YouTube URL
 * @returns Thumbnail URL or null if not a YouTube URL
 */
export const convertYouTubeToThumbnail = url => {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }
  return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

/**
 * Detects whether a given URL is a Facebook reel/video URL
 * @param url - URL to inspect
 * @returns true when the URL looks like a Facebook reel (e.g. https://www.facebook.com/reel/<id>)
 */
export const isFacebookReelUrl = url => {
  if (!url) {
    return false;
  }
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, '');
    const normalizedPath = parsedUrl.pathname.toLowerCase();
    if (!['facebook.com', 'm.facebook.com', 'fb.watch'].includes(hostname)) {
      return false;
    }
    return normalizedPath.startsWith('/reel/') || normalizedPath.startsWith('/watch/') || normalizedPath.includes('/videos/');
  } catch {
    return false;
  }
};

/**
 * Detects whether a given URL is an Instagram reel/video URL
 * @param url - URL to inspect
 * @returns true when the URL looks like an Instagram reel (e.g. https://www.instagram.com/reel/<id>/)
 */
export const isInstagramReelUrl = url => {
  if (!url) {
    return false;
  }
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, '');
    const normalizedPath = parsedUrl.pathname.toLowerCase();
    if (hostname !== 'instagram.com') {
      return false;
    }
    return normalizedPath.startsWith('/reel/') || normalizedPath.startsWith('/p/') || normalizedPath.startsWith('/tv/');
  } catch {
    return false;
  }
};

/**
 * Detects whether a given URL is a TikTok video URL
 * @param url - URL to inspect
 * @returns true when the URL looks like a TikTok video (e.g. https://www.tiktok.com/@user/video/<id>)
 */
export const isTikTokVideoUrl = url => {
  if (!url) {
    return false;
  }
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, '');
    const normalizedPath = parsedUrl.pathname.toLowerCase();
    if (hostname !== 'tiktok.com') {
      return false;
    }
    return normalizedPath.includes('/video/');
  } catch {
    return false;
  }
};

/**
 * Detects whether a given URL is a Vimeo video URL
 * @param url - URL to inspect
 * @returns true when the URL looks like a Vimeo video (e.g. https://vimeo.com/<id>)
 */
export const isVimeoVideoUrl = url => {
  if (!url) {
    return false;
  }
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, '');
    if (hostname !== 'vimeo.com' && hostname !== 'player.vimeo.com') {
      return false;
    }
    return /\d+/.test(parsedUrl.pathname);
  } catch {
    return false;
  }
};
//# sourceMappingURL=media-alternative-rule-thumbnail-utils.js.map